<template>
  <div class="tw-flex tw-flex-col tw-h-full tw-bg-white tw-rounded-lg tw-shadow-sm tw-px-2 tw-py-4">
    <!-- Table Controls Header -->
    <div class="tw-flex tw-justify-between tw-items-center">
      <div class="tw-flex-1">
        <slot name="config-manager"></slot>
      </div>
      <div class="tw-flex tw-gap-3 tw-mb-4">
        <div class="tw-relative">
          <slot name="actions"></slot>
        </div>
        <FancyTableFilters v-if="enableFilters" :table="table" />
        <FancyTableSettings v-if="enableColumnVisibility" :table="table" />
      </div>
    </div>

    <div class="tw-flex tw-flex-col tw-relative">
      <!-- Loading Overlay (when table has data) -->
      <div
        v-if="loading && !isTableEmpty"
        class="tw-absolute tw-inset-0 tw-bg-gray-300/20 tw-backdrop-blur-sm tw-z-50 tw-flex tw-items-center tw-justify-center tw-rounded-lg"
      >
        <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
          <div
            class="tw-animate-spin tw-h-8 tw-w-8 tw-border-4 tw-border-primary-200 tw-border-t-primary-600 tw-rounded-full"
          ></div>
          <span class="tw-text-sm tw-text-gray-600">{{ t('common.data.loading') }}</span>
        </div>
      </div>

      <div
        ref="tableContainer"
        class="tw-overflow-x-auto tw-overflow-y-visible tw-shadow-sm"
        :style="{
          contain: 'layout',
          maxHeight:
            props.enableDynamicHeight && availableHeight > 0 ? `${availableHeight}px` : '60vh',
        }"
      >
        <table
          :style="{
            width: `${tableWidth}px`,
            tableLayout: 'fixed',
            borderCollapse: 'separate',
            borderSpacing: '0',
          }"
        >
          <thead class="tw-sticky tw-top-0 tw-z-30 tw-bg-white tw-shadow-sm">
            <!-- Main header row -->
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-if="enableRowSelection"
                :class="[
                  'tw-w-12 tw-px-4 tw-py-3 tw-text-left',
                  // Apply pinning styles when selection column should be pinned
                  shouldPinSelectionColumn
                    ? 'tw-sticky tw-z-20 tw-bg-white tw-left-0'
                    : 'tw-relative tw-z-10 tw-bg-white',
                  // Add right border when selection column should have it
                  shouldSelectionColumnHaveRightBorder ? 'tw-border-r tw-border-gray-200' : '',
                  // Add shadow when selection column is the last left pinned
                  shouldPinSelectionColumn && shouldSelectionColumnHaveRightBorder
                    ? 'tw-shadow-sm tw-shadow-gray-200'
                    : '',
                ]"
              >
                <div class="tw-flex tw-items-center tw-justify-center">
                  <FancyCheckbox
                    :checked="table.getIsAllRowsSelected()"
                    :indeterminate="table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()"
                    @change="table.getToggleAllRowsSelectedHandler()?.($event)"
                  />
                </div>
              </th>
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                scope="col"
                :class="[
                  'tw-px-2 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-secondary-500 tw-uppercase tw-tracking-wider tw-group',
                  'tw-box-border tw-flex-none tw-overflow-hidden tw-break-words tw-min-h-0',
                  // Header bottom border only when no filters to avoid double border
                  // 'tw-border-b tw-border-gray-100',
                  // Pinning classes
                  header.column.getIsPinned()
                    ? 'tw-sticky tw-z-20 tw-bg-white'
                    : 'tw-relative tw-z-10 tw-bg-white',
                  // Directional shadows for edge pinned columns
                  getPinnedColumnShadowClasses(header.column),
                  // Border on opposite side of pin position
                  header.column.getIsPinned() === 'left' ? 'tw-border-r tw-border-gray-200' : '',
                  header.column.getIsPinned() === 'right' ? 'tw-border-l tw-border-gray-200' : '',
                ]"
                :style="{
                  width: `${getAdjustedColumnSize(header.column)}px`,
                  // Dynamic positioning
                  left:
                    header.column.getIsPinned() === 'left'
                      ? `${getAdjustedColumnLeftPosition(header.column)}px`
                      : undefined,
                  right:
                    header.column.getIsPinned() === 'right'
                      ? `${header.column.getAfter('right')}px`
                      : undefined,
                }"
              >
                <div
                  class="tw-flex tw-items-start tw-gap-2 tw-text-secondary-300 tw-flex-1 tw-pr-2 tw-min-w-0"
                >
                  <!-- Sort button -->
                  <button
                    v-if="header.column.getCanSort()"
                    @click="header.column.getToggleSortingHandler()?.($event)"
                    class="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded tw-border tw-border-gray-300 hover:tw-border-secondary-400 hover:tw-bg-secondary-50 tw-transition-colors tw-flex-shrink-0"
                    :class="{
                      'tw-bg-secondary-100 tw-border-secondary-400': header.column.getIsSorted(),
                    }"
                    :title="`Sort ${header.column.columnDef.header}`"
                  >
                    <span class="tw-text-xs tw-text-secondary-600">
                      {{
                        header.column.getIsSorted() === 'asc'
                          ? '↑'
                          : header.column.getIsSorted() === 'desc'
                          ? '↓'
                          : '↕'
                      }}
                    </span>
                  </button>

                  <!-- Header content -->
                  <FlexRender
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                </div>

                <!-- Column resize handle -->
                <div
                  v-if="canColumnResize(header.column) && enableColumnResizing"
                  class="tw-absolute tw-top-0 tw-right-0 tw-w-2 tw-h-full tw-cursor-col-resize tw-bg-gray-100 tw-z-30 tw-opacity-0 group-hover:tw-opacity-100 hover:tw-opacity-100"
                  :class="{
                    'tw-transition-opacity tw-duration-200': !header.column.getIsResizing(),
                    'tw-opacity-100': header.column.getIsResizing(),
                  }"
                  @mousedown="event => handleResizeMouseDown(event, header)"
                  @touchstart="event => handleResizeMouseDown(event, header)"
                  @click.stop
                >
                  <!-- Subtle resize indicator -->
                  <div
                    class="tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-gray-300 group-hover:tw-bg-gray-400 tw-transition-colors tw-duration-200"
                  ></div>
                </div>
              </th>
            </tr>
          </thead>

          <tbody class="tw-bg-white" style="max-height: 100px !important">
            <!-- Empty State Row -->
            <tr v-if="isTableEmpty && !props.loading" class="tw-border-t tw-border-gray-100">
              <td
                :colspan="
                  enableRowSelection
                    ? (table.getAllColumns()?.length || 0) + 1
                    : table.getAllColumns()?.length || 0
                "
                class="tw-px-4 tw-py-8 tw-text-sm tw-text-center tw-text-gray-500"
              >
                {{ t('common.data.no_data') }}
              </td>
            </tr>

            <!-- Loading Skeleton Rows -->
            <template v-if="props.loading && isTableEmpty">
              <tr v-for="i in 12" :key="`skeleton-${i}`" class="tw-border-t tw-border-gray-100">
                <!-- Selection column skeleton -->
                <td v-if="enableRowSelection" class="tw-w-12 tw-px-4 tw-py-3">
                  <div class="tw-flex tw-items-center tw-justify-center">
                    <Skeleton class="tw-h-4 tw-w-4 tw-rounded" />
                  </div>
                </td>

                <!-- Column skeletons -->
                <td
                  v-for="column in table.getVisibleFlatColumns()"
                  :key="`skeleton-${i}-${column.id}`"
                  class="tw-py-3 tw-text-sm"
                  :class="{
                    'tw-pl-8': column.getIsPinned() !== 'right',
                    'tw-px-4': column.getIsPinned() === 'right',
                  }"
                >
                  <Skeleton
                    class="tw-h-4"
                    :class="{
                      'tw-w-full tw-max-w-32': column.getSize() > 150,
                      'tw-w-20': column.getSize() <= 150,
                    }"
                  />
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <tr
              v-else-if="!isTableEmpty"
              v-for="row in table.getRowModel().rows"
              :key="row.id"
              class="tw-group hover:tw-bg-gray-50 tw-transition-colors tw-px-4 tw-py-3"
            >
              <td
                v-if="enableRowSelection"
                :class="[
                  'tw-w-12 tw-px-4 tw-py-3',
                  // Row borders - only top border to avoid double borders
                  'tw-border-t tw-border-gray-100',
                  // Bottom border only for the last row
                  row.index === table.getRowModel().rows.length - 1
                    ? 'tw-border-b tw-border-gray-100'
                    : '',
                  // Apply pinning styles when selection column should be pinned
                  shouldPinSelectionColumn
                    ? 'tw-sticky tw-z-20 tw-bg-white group-hover:tw-bg-gray-100 tw-left-0'
                    : 'tw-relative tw-z-10',
                  // Add right border when selection column should have it
                  shouldSelectionColumnHaveRightBorder ? 'tw-border-r tw-border-gray-200' : '',
                  // Add shadow when selection column is the last left pinned
                  shouldPinSelectionColumn && shouldSelectionColumnHaveRightBorder
                    ? 'tw-shadow-sm tw-shadow-gray-200'
                    : '',
                ]"
              >
                <div class="tw-flex tw-items-center tw-justify-center">
                  <FancyCheckbox
                    :checked="row.getIsSelected()"
                    :disabled="!row.getCanSelect()"
                    @change="row.getToggleSelectedHandler()?.($event)"
                  />
                </div>
              </td>
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :class="[
                  'tw-py-3 tw-text-sm tw-text-gray-900',
                  'tw-box-border tw-flex-none tw-overflow-hidden',
                  // Conditional text wrapping behavior
                  props.enableCellTextWrapping
                    ? 'tw-break-words tw-min-h-0'
                    : 'tw-whitespace-nowrap tw-text-ellipsis',
                  // Row borders - only top border to avoid double borders
                  'tw-border-t tw-border-gray-100',
                  // Bottom border only for the last row
                  row.index === table.getRowModel().rows.length - 1
                    ? 'tw-border-b tw-border-gray-100'
                    : '',
                  // Pinning classes with hover effects
                  cell.column.getIsPinned()
                    ? 'tw-sticky tw-z-20 tw-bg-white group-hover:tw-bg-gray-100'
                    : 'tw-relative tw-z-10',
                  // Directional shadows for edge pinned columns
                  getPinnedColumnShadowClasses(cell.column),
                  // Border on opposite side of pin position
                  cell.column.getIsPinned() === 'left' ? 'tw-border-r tw-border-gray-200' : '',
                  cell.column.getIsPinned() === 'right' ? 'tw-border-l tw-border-gray-200' : '',
                  cell.column.getIsPinned() === 'right' ? 'tw-px-4' : 'tw-pl-8',
                ]"
                :style="{
                  width: `${getAdjustedColumnSize(cell.column)}px`,
                  // Dynamic positioning
                  left:
                    cell.column.getIsPinned() === 'left'
                      ? `${getAdjustedColumnLeftPosition(cell.column)}px`
                      : undefined,
                  right:
                    cell.column.getIsPinned() === 'right'
                      ? `${cell.column.getAfter('right')}px`
                      : undefined,
                }"
              >
                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="enablePagination && !isTableEmpty"
        class="tw-flex tw-justify-center tw-items-center tw-px-4 tw-pt-2 tw-bg-white tw-rounded-b-lg tw-mt-2"
      >
        <FancyPagination
          class="tw-w-full"
          :table="table"
          :page-sizes="paginationSizes"
          :compact="compactPagination"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FancyPagination from '@/components/shared/fancyTables/FancyPagination.vue';
import FancyTableFilters from '@/components/shared/fancyTables/FancyTableFilters.vue';
import FancyTableSettings from '@/components/shared/fancyTables/FancyTableSettings.vue';
import type {
  FancyTableColumnDef,
  SerializedTableState,
} from '@/components/shared/fancyTables/types';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FlexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/vue-table';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FancyCheckbox from './FancyCheckbox.vue';
import {
  booleanFilterFn,
  dateRangeFilterFn,
  enumFilterFn,
  inNumberRangeFilterFn,
  moneySortingFn,
  ibanFilterFn,
  ordersArrayFilterFn,
  arrayIncludesPartialFilterFn,
} from './filters';

// Composable for calculating available height
const useAvailableHeight = (enablePagination: boolean, enableDynamicHeight: boolean) => {
  const availableHeight = ref<number>(0);

  const calculateAvailableHeight = () => {
    // If dynamic height is disabled, return a default value
    if (!enableDynamicHeight) {
      availableHeight.value = 0;
      return;
    }

    // Get viewport height
    const viewportHeight = window.innerHeight;

    // NavBar height (fixed at 64px based on tw-h-16)
    const navbarHeight = 64;

    // HijackBanner height (if present, based on line-height: 50px)
    const hijackBannerHeight = document.querySelector('.marquee') ? 50 : 0;

    // Main content padding (1.5rem = 24px top and bottom)
    const mainContentPadding = 48;

    // Table container padding (tw-p-4 = 16px top and bottom)
    const tableContainerPadding = 32;

    // Table controls header height (approximate)
    const tableControlsHeight = 60;

    // Pagination height (if enabled)
    const paginationHeight = enablePagination ? 60 : 0;

    // Calculate available height
    const calculatedHeight =
      viewportHeight -
      navbarHeight -
      hijackBannerHeight -
      mainContentPadding -
      tableContainerPadding -
      tableControlsHeight -
      paginationHeight;

    // Ensure minimum height and subtract some buffer for safety
    availableHeight.value = Math.max(calculatedHeight - 40, 300);
  };

  onMounted(() => {
    calculateAvailableHeight();
    window.addEventListener('resize', calculateAvailableHeight);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', calculateAvailableHeight);
  });

  return {
    availableHeight,
    calculateAvailableHeight,
  };
};

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    data: any[];
    loading?: boolean;
    columns: FancyTableColumnDef<any>[];
    enableFilters?: boolean;
    enablePagination?: boolean;
    enableColumnVisibility?: boolean;
    enableColumnResizing?: boolean;
    enableColumnPinning?: boolean;
    enableCellTextWrapping?: boolean;
    /**
     * Enable dynamic height calculation based on available viewport space.
     * When disabled, falls back to 60vh max-height.
     * Useful when table is used in modals, cards, or other containers.
     * @default true
     */
    enableDynamicHeight?: boolean;
    columnResizeMode?: 'onChange' | 'onEnd';
    paginationSizes?: number[];
    /** Compact pagination: prev / "X of Y" / next on one line (no numbered pages). For narrow/embedded tables. */
    compactPagination?: boolean;
    initialColumnVisibility?: VisibilityState;
    initialColumnOrder?: ColumnOrderState;
    initialColumnSizing?: ColumnSizingState;
    initialColumnPinning?: ColumnPinningState;
    initialSorting?: SortingState;
    enableRowSelection?: boolean;
    initialRowSelection?: RowSelectionState;
    getRowId?: (row: any) => string;
    initialColumnFilters?: ColumnFiltersState;
    initialPageSize?: number;
    initialGlobalFilter?: string;
  }>(),
  {
    loading: false,
    enableFilters: true,
    enablePagination: true,
    enableColumnVisibility: true,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableCellTextWrapping: false,
    enableDynamicHeight: true,
    columnResizeMode: 'onChange',
    paginationSizes: () => [10, 25, 50, 100],
    initialColumnVisibility: () => ({}),
    initialColumnFilters: () => [],
    initialSorting: () => [],
    initialColumnOrder: () => [],
    initialColumnSizing: () => ({}),
    initialColumnPinning: () => ({ left: [], right: [] }),
    enableRowSelection: false,
    initialRowSelection: () => ({}),
    initialPageSize: 10,
    initialGlobalFilter: '',
  }
);

const emit = defineEmits<{
  'update:sorting': [sorting: SortingState];
  'update:columnFilters': [filters: ColumnFiltersState];
  'update:columnVisibility': [visibility: VisibilityState];
  'update:columnOrder': [order: ColumnOrderState];
  'update:columnSizing': [sizing: ColumnSizingState];
  'update:columnPinning': [pinning: ColumnPinningState];
  'update:rowSelection': [selection: any[]];
  'update:state': [state: SerializedTableState];
}>();

// Use the available height composable only when dynamic height is enabled
const { availableHeight, calculateAvailableHeight } = useAvailableHeight(
  props.enablePagination,
  props.enableDynamicHeight
);

// State management
const sorting = ref<SortingState>(props.initialSorting);
const columnFilters = ref<ColumnFiltersState>(props.initialColumnFilters);
const columnVisibility = ref<VisibilityState>(props.initialColumnVisibility);
const columnOrder = ref<ColumnOrderState>(props.initialColumnOrder);
const columnSizing = ref<ColumnSizingState>(props.initialColumnSizing);
const columnPinning = ref<ColumnPinningState>(props.initialColumnPinning);
const rowSelection = ref<RowSelectionState>(props.initialRowSelection);
const pagination = ref({
  pageIndex: 0,
  pageSize: props.initialPageSize || 10,
});
const filter = ref<string>(props.initialGlobalFilter || '');

// Create a computed property for the table data to ensure reactivity
const tableData = computed(() => props.data);

// Check if table is empty
const isTableEmpty = computed(() => {
  return tableData.value.length === 0;
});

// Container width tracking
const containerWidth = ref<number>(0);

// Update container width
const updateContainerWidth = () => {
  if (tableContainer.value) {
    const rect = tableContainer.value.getBoundingClientRect();
    containerWidth.value = Math.floor(rect.width);
  }
};

// Get the column that should fill remaining space
const getFillColumn = computed(() => {
  const visibleColumns = table.getVisibleFlatColumns();
  const pinning = table.getState().columnPinning;
  const rightPinnedColumns = pinning.right || [];

  if (rightPinnedColumns.length > 0) {
    // Find the last non-pinned column
    const nonPinnedColumns = visibleColumns.filter(col => !col.getIsPinned());
    return nonPinnedColumns[nonPinnedColumns.length - 1];
  } else {
    // Last visible column
    return visibleColumns[visibleColumns.length - 1];
  }
});

// Calculate adjusted column sizes to fill container
const adjustedColumnSizes = computed(() => {
  const visibleColumns = table.getVisibleFlatColumns();
  const totalNaturalSize = visibleColumns.reduce((sum, col) => sum + col.getSize(), 0);

  // Account for selection column width when enabled
  const selectionColumnSize = props.enableRowSelection ? selectionColumnWidth : 0;
  const totalWidthWithSelection = totalNaturalSize + selectionColumnSize;

  // Use container width as the target width, but ensure we don't go below the natural size
  const targetWidth = Math.max(containerWidth.value, totalWidthWithSelection);
  const fillColumn = getFillColumn.value;

  if (!fillColumn || targetWidth <= totalWidthWithSelection) {
    // No adjustment needed
    return new Map(visibleColumns.map(col => [col.id, col.getSize()]));
  }

  // Calculate extra space to distribute (accounting for selection column)
  const extraSpace = targetWidth - totalWidthWithSelection;
  const adjustedSizes = new Map<string, number>();

  visibleColumns.forEach(col => {
    if (col.id === fillColumn.id) {
      adjustedSizes.set(col.id, col.getSize() + extraSpace);
    } else {
      adjustedSizes.set(col.id, col.getSize());
    }
  });

  return adjustedSizes;
});

// Force reactive table width calculation
const tableWidth = computed(() => {
  try {
    const baseTableSize = table.getTotalSize();
    const selectionColumnSize = props.enableRowSelection ? selectionColumnWidth : 0;
    const totalTableSize = baseTableSize + selectionColumnSize;

    // When selection column is enabled, be more conservative with the width
    if (props.enableRowSelection) {
      return Math.max(containerWidth.value, totalTableSize);
    } else {
      return Math.max(containerWidth.value, totalTableSize, 100);
    }
  } catch {
    const selectionColumnSize = props.enableRowSelection ? selectionColumnWidth : 0;
    return Math.max(containerWidth.value, 100 + selectionColumnSize);
  }
});

// Helper functions for pinned column shadows
const isLastLeftPinnedColumn = (columnId: string): boolean => {
  const pinning = table.getState().columnPinning;
  const leftPinned = pinning.left || [];
  return leftPinned.length > 0 && leftPinned[leftPinned.length - 1] === columnId;
};

const isFirstRightPinnedColumn = (columnId: string): boolean => {
  const pinning = table.getState().columnPinning;
  const rightPinned = pinning.right || [];
  return rightPinned.length > 0 && rightPinned[0] === columnId;
};

// Check if selection column should be treated as pinned
const shouldPinSelectionColumn = computed(() => {
  if (!props.enableRowSelection) return false;
  const pinning = table.getState().columnPinning;
  return (pinning.left && pinning.left.length > 0) || (pinning.right && pinning.right.length > 0);
});

// Check if selection column should have right border (when it's the last left pinned or when there are no left pinned but there are right pinned)
const shouldSelectionColumnHaveRightBorder = computed(() => {
  if (!shouldPinSelectionColumn.value) return false;
  const pinning = table.getState().columnPinning;
  const leftPinned = pinning.left || [];
  const rightPinned = pinning.right || [];

  // Selection column should have right border and shadow only when:
  // 1. There are no left pinned columns but there are right pinned columns (selection column is the separator)
  // When there are left pinned columns, the actual last left pinned column should have the border/shadow
  return leftPinned.length === 0 && rightPinned.length > 0;
});

// Get the width of the selection column (fixed width)
const selectionColumnWidth = 48; // tw-w-12 = 48px

// Update positioning calculations to account for selection column
const getAdjustedColumnLeftPosition = (column: any): number => {
  const basePosition = column.getStart('left');
  if (shouldPinSelectionColumn.value && column.getIsPinned() === 'left') {
    return basePosition + selectionColumnWidth;
  }
  return basePosition;
};

const getPinnedColumnShadowClasses = (column: any): string => {
  const isPinned = column.getIsPinned();
  if (!isPinned) return '';

  if (isPinned === 'left' && isLastLeftPinnedColumn(column.id)) {
    // If there's a pinned selection column, the shadow should appear on the last left-pinned regular column
    // not on the selection column itself
    return 'tw-shadow-sm tw-shadow-gray-200';
  }

  if (isPinned === 'right' && isFirstRightPinnedColumn(column.id)) {
    return 'tw-shadow-sm tw-shadow-gray-200';
  }

  return '';
};

// Handle resize events to prevent sorting interference
const handleResizeMouseDown = (event: MouseEvent | TouchEvent, header: any) => {
  event.stopPropagation(); // Prevent sorting from triggering

  // Disable transitions during resize for better performance
  const tableElement = (event.target as HTMLElement)?.closest('table') as HTMLTableElement;
  if (tableElement) {
    tableElement.style.transition = 'none';
  }

  const resizeHandler = header.getResizeHandler();
  if (resizeHandler) {
    resizeHandler(event);

    // Re-enable transitions after resize is complete
    const onResizeEnd = () => {
      if (tableElement) {
        tableElement.style.transition = '';
      }
      document.removeEventListener('mouseup', onResizeEnd);
      document.removeEventListener('touchend', onResizeEnd);
    };

    document.addEventListener('mouseup', onResizeEnd);
    document.addEventListener('touchend', onResizeEnd);
  }
};

// Table configuration
const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns: props.columns as FancyTableColumnDef<any>[],
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  enableColumnResizing: props.enableColumnResizing,
  enableColumnPinning: props.enableColumnPinning,
  columnResizeMode: props.columnResizeMode,
  enableRowSelection: props.enableRowSelection,
  enableSortingRemoval: true,
  enableMultiSort: false,
  getRowId: props.getRowId,
  filterFns: {
    dateRange: dateRangeFilterFn,
    boolean: booleanFilterFn,
    inNumberRange: inNumberRangeFilterFn,
    enumFilter: enumFilterFn,
    iban: ibanFilterFn,
    ordersArray: ordersArrayFilterFn,
    arrayIncludesPartial: arrayIncludesPartialFilterFn,
  },
  sortingFns: {
    money: moneySortingFn,
  },
  defaultColumn: {
    size: 150,
    minSize: 100,
    maxSize: 800,
  },
  columnResizeDirection: 'ltr',
  autoResetPageIndex: false,
  state: {
    get sorting() {
      return sorting.value;
    },
    get columnFilters() {
      return columnFilters.value;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
    get columnOrder() {
      return columnOrder.value;
    },
    get columnSizing() {
      return columnSizing.value;
    },
    get columnPinning() {
      return columnPinning.value;
    },
    get rowSelection() {
      return rowSelection.value;
    },
    get pagination() {
      return pagination.value;
    },
    get globalFilter() {
      return filter.value;
    },
  },
  onSortingChange: updaterOrValue => {
    sorting.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting.value) : updaterOrValue;

    // Reset to first page when sorting changes to ensure consistent behavior
    table.setPageIndex(0);

    emit('update:sorting', sorting.value);
  },
  onColumnFiltersChange: updaterOrValue => {
    columnFilters.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(columnFilters.value) : updaterOrValue;

    // Reset to first page when filters change to ensure results are visible
    table.setPageIndex(0);

    emit('update:columnFilters', columnFilters.value);
  },
  onColumnVisibilityChange: updaterOrValue => {
    const oldVisibility = columnVisibility.value;
    columnVisibility.value =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(columnVisibility.value)
        : updaterOrValue;

    // Clear filters for columns that become hidden
    const newVisibility = columnVisibility.value;
    const hiddenColumns = Object.keys(newVisibility).filter(
      columnId => newVisibility[columnId] === false
    );

    // Find columns that were just hidden (weren't hidden before)
    const newlyHiddenColumns = hiddenColumns.filter(columnId => oldVisibility[columnId] !== false);

    // Clear filters for newly hidden columns
    newlyHiddenColumns.forEach(columnId => {
      const column = table.getColumn(columnId);
      if (column && column.getCanFilter()) {
        column.setFilterValue(undefined);
      }
    });

    emit('update:columnVisibility', columnVisibility.value);
  },
  onColumnOrderChange: updaterOrValue => {
    columnOrder.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(columnOrder.value) : updaterOrValue;
    emit('update:columnOrder', columnOrder.value);
    emit('update:state', getSerializedState());
  },
  onColumnSizingChange: updaterOrValue => {
    const newValue =
      typeof updaterOrValue === 'function' ? updaterOrValue(columnSizing.value) : updaterOrValue;

    // Only update if the value actually changed to prevent unnecessary re-renders
    if (JSON.stringify(columnSizing.value) !== JSON.stringify(newValue)) {
      columnSizing.value = newValue;
      emit('update:columnSizing', newValue);
    }
  },
  onColumnPinningChange: updaterOrValue => {
    columnPinning.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(columnPinning.value) : updaterOrValue;
    emit('update:columnPinning', columnPinning.value);
  },
  onRowSelectionChange: updaterOrValue => {
    rowSelection.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection.value) : updaterOrValue;
    emit(
      'update:rowSelection',
      table.getFilteredSelectedRowModel().rows.map(row => row.original)
    );
  },
  onGlobalFilterChange: updaterOrValue => {
    filter.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(filter.value) : updaterOrValue;

    // Reset to first page when global filter changes to ensure results are visible
    table.setPageIndex(0);
  },
  onPaginationChange: updaterOrValue => {
    pagination.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(pagination.value) : updaterOrValue;

    const serializedState: SerializedTableState = getSerializedState();
    emit('update:state', serializedState);
  },
  onStateChange: () => {
    const serializedState: SerializedTableState = getSerializedState();
    emit('update:state', serializedState);
  },
});

const getSerializedState = () => {
  const state = table.getState();
  return {
    columnVisibility: state.columnVisibility,
    columnOrder: state.columnOrder,
    columnSizing: state.columnSizing,
    columnPinning: {
      left: state.columnPinning.left || [],
      right: state.columnPinning.right || [],
    },
    columnFilters: state.columnFilters,
    globalFilter: state.globalFilter,
    sorting: state.sorting,
    pageSize: state.pagination.pageSize,
  };
};

// Handle indeterminate state for checkboxes
const updateIndeterminateState = () => {
  nextTick(() => {
    const headerCheckbox = document.querySelector(
      'thead input[type="checkbox"]'
    ) as HTMLInputElement;
    if (headerCheckbox) {
      headerCheckbox.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected();
    }
  });
};

// Watch for row selection changes to update indeterminate state
watch(
  () => table.getState().rowSelection,
  () => {
    updateIndeterminateState();
  },
  { deep: true }
);

const tableContainer = ref<HTMLDivElement | null>(null);

const getAdjustedColumnSize = (column: any): number => {
  const adjustedSize = adjustedColumnSizes.value.get(column.id);
  return adjustedSize !== undefined ? adjustedSize : column.getSize();
};

const canColumnResize = (column: any): boolean => {
  if (!column.getCanResize() || !props.enableColumnResizing) {
    return false;
  }

  const fillColumn = getFillColumn.value;

  // Fill column can be resized in large tables (when table width exceeds container width)
  // but not in small tables where it needs to fill the remaining space
  if (fillColumn && column.id === fillColumn.id) {
    const isLargeTable = tableWidth.value > containerWidth.value;
    return isLargeTable;
  }

  // All other columns (including right-pinned) can be resized
  return true;
};

// ResizeObserver for container width tracking
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (tableContainer.value) {
    // Initial measurement
    updateContainerWidth();

    // Set up ResizeObserver for dynamic width tracking
    resizeObserver = new ResizeObserver(() => {
      updateContainerWidth();
    });
    resizeObserver.observe(tableContainer.value);
  }

  // Initialize indeterminate state
  updateIndeterminateState();
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

// Watch for table state changes that might affect layout
watch(
  [
    () => table.getState().columnPinning,
    () => table.getState().columnVisibility,
    () => table.getState().columnOrder,
  ],
  () => {
    nextTick(() => {
      updateContainerWidth();
    });
  },
  { deep: true }
);

// Watch for pagination and dynamic height changes to recalculate height
watch([() => props.enablePagination, () => props.enableDynamicHeight], () => {
  nextTick(() => {
    calculateAvailableHeight();
  });
});

const updateTableState = (state: SerializedTableState) => {
  table.setSorting(state.sorting);
  table.setColumnFilters(state.columnFilters);
  table.setColumnVisibility(state.columnVisibility);
  table.setColumnOrder(state.columnOrder);
  table.setColumnSizing(state.columnSizing);
  table.setColumnPinning(state.columnPinning);
  table.setGlobalFilter(state.globalFilter);
  if (state.pageSize && state.pageSize !== pagination.value.pageSize) {
    table.setPageSize(state.pageSize);
    table.setPageIndex(0); // Always reset to first page when applying saved config
  }
};
// Expose methods for parent components
defineExpose({
  resetRowSelection: () => {
    rowSelection.value = {};
  },
  getSerializedState,
  updateTableState,
});
</script>
